import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { 
  Users, 
  UserPlus, 
  DollarSign, 
  Percent, 
  Calculator,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  Building2,
  Briefcase,
  TrendingUp,
  Download,
  Edit,
  Trash2,
  Send,
  Crown,
  Shield
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

/**
 * Staff Management Dashboard - Elite Pro Feature
 * 
 * Includes:
 * - Commissioned Staff (barbers/stylists) with split calculations
 * - Support Staff (hourly/flat - assistants, managers)
 * - Payroll tracking with Stripe integration
 * - Tax compliance ($600+ W-9 collection)
 */
const StaffManagement = ({ token }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("commissioned");
  const [staff, setStaff] = useState([]);
  const [supportStaff, setSupportStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [payrollSummary, setPayrollSummary] = useState(null);

  // New staff form
  const [newStaff, setNewStaff] = useState({
    name: "",
    email: "",
    phone: "",
    type: "commissioned", // commissioned, hourly, flat
    position_title: "",
    // For commissioned
    commission_type: "percentage", // percentage or fixed
    commission_rate: 60, // Default 60/40 split
    // For hourly/flat
    pay_rate: 0,
    pay_frequency: "weekly" // weekly, bi-weekly, monthly
  });

  useEffect(() => {
    if (token) {
      fetchStaff();
      fetchPayrollSummary();
    }
  }, [token]);

  const fetchStaff = async () => {
    try {
      const [commissionedRes, supportRes] = await Promise.all([
        axios.get(`${API}/staff/commissioned`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/staff/support`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStaff(commissionedRes.data || []);
      setSupportStaff(supportRes.data || []);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
      // Set demo data for now
      setStaff([]);
      setSupportStaff([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollSummary = async () => {
    try {
      const res = await axios.get(`${API}/staff/payroll/summary`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setPayrollSummary(res.data);
    } catch (error) {
      // Set default summary
      setPayrollSummary({
        total_payroll_ytd: 0,
        total_commissions_ytd: 0,
        staff_requiring_w9: [],
        next_payout_date: null
      });
    }
  };

  const handleAddStaff = async () => {
    try {
      const endpoint = newStaff.type === "commissioned" ? "/staff/commissioned" : "/staff/support";
      const payload = {
        name: newStaff.name,
        email: newStaff.email,
        phone: newStaff.phone,
        position_title: newStaff.position_title || (newStaff.type === "commissioned" ? "Stylist" : "Assistant"),
        ...(newStaff.type === "commissioned" ? {
          commission_type: newStaff.commission_type,
          commission_rate: parseFloat(newStaff.commission_rate)
        } : {
          pay_type: newStaff.type, // hourly or flat
          pay_rate: parseFloat(newStaff.pay_rate),
          pay_frequency: newStaff.pay_frequency
        })
      };

      await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${newStaff.name} added successfully!`);
      setShowAddModal(false);
      setNewStaff({
        name: "", email: "", phone: "", type: "commissioned",
        position_title: "", commission_type: "percentage",
        commission_rate: 60, pay_rate: 0, pay_frequency: "weekly"
      });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add staff member");
    }
  };

  const handlePayout = async (staffMember) => {
    setSelectedStaff(staffMember);
    setShowPayoutModal(true);
  };

  const processStripePayout = async () => {
    try {
      await axios.post(`${API}/staff/payout`, {
        staff_id: selectedStaff.staff_id,
        amount: selectedStaff.pending_payout || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payout of $${selectedStaff.pending_payout?.toFixed(2)} sent to ${selectedStaff.name}`);
      setShowPayoutModal(false);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payout failed");
    }
  };

  const calculateCommission = (grossAmount, commissionType, rate) => {
    if (commissionType === "percentage") {
      return grossAmount * (rate / 100);
    }
    return rate; // Fixed amount per service
  };

  // Tax compliance check
  const needsW9 = (ytdEarnings) => ytdEarnings >= 600;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-purple-400" />
            Staff Management
          </h2>
          <p className="text-stone-400 mt-1">Manage your team, commissions, and payroll</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            <Crown className="w-3 h-3 mr-1" />
            Elite Feature
          </Badge>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-purple-500 hover:bg-purple-600"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Staff
          </Button>
        </div>
      </div>

      {/* Payroll Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-stone-400 text-xs">YTD Payroll</p>
                <p className="text-xl font-bold text-white">
                  ${payrollSummary?.total_payroll_ytd?.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-stone-400 text-xs">YTD Commissions</p>
                <p className="text-xl font-bold text-white">
                  ${payrollSummary?.total_commissions_ytd?.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-stone-400 text-xs">Total Staff</p>
                <p className="text-xl font-bold text-white">
                  {staff.length + supportStaff.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-stone-700 ${payrollSummary?.staff_requiring_w9?.length > 0 ? 'bg-amber-900/30 border-amber-500/50' : 'bg-stone-800/50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${payrollSummary?.staff_requiring_w9?.length > 0 ? 'bg-amber-500/20' : 'bg-stone-700'}`}>
                <FileText className={`w-5 h-5 ${payrollSummary?.staff_requiring_w9?.length > 0 ? 'text-amber-400' : 'text-stone-400'}`} />
              </div>
              <div>
                <p className="text-stone-400 text-xs">W-9 Required</p>
                <p className={`text-xl font-bold ${payrollSummary?.staff_requiring_w9?.length > 0 ? 'text-amber-400' : 'text-white'}`}>
                  {payrollSummary?.staff_requiring_w9?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tax Compliance Alert */}
      {payrollSummary?.staff_requiring_w9?.length > 0 && (
        <Card className="bg-amber-900/30 border-amber-500/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-400 font-medium">Tax Compliance Required</p>
                <p className="text-stone-300 text-sm">
                  {payrollSummary.staff_requiring_w9.length} staff member(s) have earned over $600 this year. 
                  Collect W-9 forms to ensure tax compliance.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {payrollSummary.staff_requiring_w9.map((name, idx) => (
                    <Badge key={idx} className="bg-amber-500/20 text-amber-300">
                      {name}
                    </Badge>
                  ))}
                </div>
                <Button className="mt-3 bg-amber-500 hover:bg-amber-600 text-black" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Send W-9 Requests
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-stone-800">
          <TabsTrigger value="commissioned" className="data-[state=active]:bg-purple-500">
            <Percent className="w-4 h-4 mr-2" />
            Commissioned ({staff.length})
          </TabsTrigger>
          <TabsTrigger value="support" className="data-[state=active]:bg-blue-500">
            <Briefcase className="w-4 h-4 mr-2" />
            Support Staff ({supportStaff.length})
          </TabsTrigger>
          <TabsTrigger value="payroll" className="data-[state=active]:bg-green-500">
            <DollarSign className="w-4 h-4 mr-2" />
            Payroll
          </TabsTrigger>
        </TabsList>

        {/* Commissioned Staff Tab */}
        <TabsContent value="commissioned">
          {staff.length === 0 ? (
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Commissioned Staff</h3>
                <p className="text-stone-400 text-sm mb-4">
                  Add barbers or stylists who earn commission on their services.
                </p>
                <Button onClick={() => { setNewStaff({...newStaff, type: "commissioned"}); setShowAddModal(true); }}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Commissioned Staff
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {staff.map((member) => (
                <Card key={member.staff_id} className="bg-stone-800/50 border-stone-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                          <span className="text-purple-400 font-bold text-lg">
                            {member.name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{member.name}</h4>
                          <p className="text-stone-400 text-sm">{member.position_title || "Stylist"}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                              {member.commission_type === "percentage" 
                                ? `${member.commission_rate}% Split`
                                : `$${member.commission_rate} per service`
                              }
                            </Badge>
                            {needsW9(member.ytd_earnings || 0) && (
                              <Badge className="bg-amber-500/20 text-amber-300 text-xs">
                                W-9 Required
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-stone-400 text-xs">YTD Earnings</p>
                        <p className="text-xl font-bold text-white">
                          ${(member.ytd_earnings || 0).toLocaleString()}
                        </p>
                        {member.pending_payout > 0 && (
                          <Badge className="bg-green-500/20 text-green-400 mt-1">
                            ${member.pending_payout?.toFixed(2)} pending
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="outline" size="sm" className="border-stone-600">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handlePayout(member)}
                          disabled={!member.pending_payout || member.pending_payout <= 0}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Pay
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Support Staff Tab */}
        <TabsContent value="support">
          {supportStaff.length === 0 ? (
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-8 text-center">
                <Briefcase className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Support Staff</h3>
                <p className="text-stone-400 text-sm mb-4">
                  Add assistants, shampoo techs, or managers with hourly or flat pay.
                </p>
                <Button onClick={() => { setNewStaff({...newStaff, type: "hourly"}); setShowAddModal(true); }}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Support Staff
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {supportStaff.map((member) => (
                <Card key={member.staff_id} className="bg-stone-800/50 border-stone-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <span className="text-blue-400 font-bold text-lg">
                            {member.name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{member.name}</h4>
                          <p className="text-stone-400 text-sm">{member.position_title}</p>
                          <Badge className="bg-blue-500/20 text-blue-300 text-xs mt-1">
                            {member.pay_type === "hourly" 
                              ? `$${member.pay_rate}/hr`
                              : `$${member.pay_rate}/${member.pay_frequency}`
                            }
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-stone-400 text-xs">YTD Earnings</p>
                        <p className="text-xl font-bold text-white">
                          ${(member.ytd_earnings || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="outline" size="sm" className="border-stone-600">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handlePayout(member)}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Pay
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calculator className="w-5 h-5 text-green-400" />
                Payroll Overview
              </CardTitle>
              <CardDescription className="text-stone-400">
                Track and manage all staff payments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payment Methods */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-stone-900/50 border-stone-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <CreditCard className="w-5 h-5 text-purple-400" />
                      <h4 className="text-white font-medium">Stripe Direct Payouts</h4>
                    </div>
                    <p className="text-stone-400 text-sm mb-3">
                      Pay staff directly to their bank accounts via Stripe Connect.
                    </p>
                    <Badge className="bg-green-500/20 text-green-400">Connected</Badge>
                  </CardContent>
                </Card>

                <Card className="bg-stone-900/50 border-stone-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="w-5 h-5 text-blue-400" />
                      <h4 className="text-white font-medium">Manual Tracking</h4>
                    </div>
                    <p className="text-stone-400 text-sm mb-3">
                      Record cash or check payments for accurate bookkeeping.
                    </p>
                    <Button variant="outline" size="sm" className="border-stone-600 text-white">
                      Record Payment
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Export */}
              <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                <Button variant="outline" className="border-stone-600 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Export Payroll Report
                </Button>
                <Button variant="outline" className="border-stone-600 text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Tax Summary
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Staff Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-400" />
              Add Staff Member
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Add a new team member to your staff
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Staff Type */}
            <div className="space-y-2">
              <Label>Staff Type</Label>
              <Select value={newStaff.type} onValueChange={(v) => setNewStaff({...newStaff, type: v})}>
                <SelectTrigger className="bg-stone-800 border-stone-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-stone-700">
                  <SelectItem value="commissioned">Commissioned (Stylist/Barber)</SelectItem>
                  <SelectItem value="hourly">Hourly (Support Staff)</SelectItem>
                  <SelectItem value="flat">Flat Rate (Manager/Assistant)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Basic Info */}
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input 
                value={newStaff.name}
                onChange={(e) => setNewStaff({...newStaff, name: e.target.value})}
                className="bg-stone-800 border-stone-700"
                placeholder="John Smith"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                  className="bg-stone-800 border-stone-700"
                  placeholder="john@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={newStaff.phone}
                  onChange={(e) => setNewStaff({...newStaff, phone: e.target.value})}
                  className="bg-stone-800 border-stone-700"
                  placeholder="555-1234"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Position Title</Label>
              <Input 
                value={newStaff.position_title}
                onChange={(e) => setNewStaff({...newStaff, position_title: e.target.value})}
                className="bg-stone-800 border-stone-700"
                placeholder={newStaff.type === "commissioned" ? "Senior Stylist" : "Shampoo Tech"}
              />
            </div>

            {/* Commissioned Settings */}
            {newStaff.type === "commissioned" && (
              <div className="space-y-4 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <h4 className="text-purple-400 font-medium flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Commission Settings
                </h4>
                <div className="space-y-2">
                  <Label>Commission Type</Label>
                  <Select 
                    value={newStaff.commission_type} 
                    onValueChange={(v) => setNewStaff({...newStaff, commission_type: v})}
                  >
                    <SelectTrigger className="bg-stone-800 border-stone-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-800 border-stone-700">
                      <SelectItem value="percentage">Percentage Split</SelectItem>
                      <SelectItem value="fixed">Fixed Amount per Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {newStaff.commission_type === "percentage" ? "Staff's Percentage" : "Amount per Service"}
                  </Label>
                  <div className="flex items-center gap-2">
                    {newStaff.commission_type === "fixed" && <span className="text-stone-400">$</span>}
                    <Input 
                      type="number"
                      value={newStaff.commission_rate}
                      onChange={(e) => setNewStaff({...newStaff, commission_rate: e.target.value})}
                      className="bg-stone-800 border-stone-700"
                    />
                    {newStaff.commission_type === "percentage" && <span className="text-stone-400">%</span>}
                  </div>
                  {newStaff.commission_type === "percentage" && (
                    <p className="text-stone-500 text-xs">
                      Staff gets {newStaff.commission_rate}%, you keep {100 - newStaff.commission_rate}%
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Hourly/Flat Settings */}
            {(newStaff.type === "hourly" || newStaff.type === "flat") && (
              <div className="space-y-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <h4 className="text-blue-400 font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Pay Settings
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{newStaff.type === "hourly" ? "Hourly Rate" : "Pay Amount"}</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-stone-400">$</span>
                      <Input 
                        type="number"
                        value={newStaff.pay_rate}
                        onChange={(e) => setNewStaff({...newStaff, pay_rate: e.target.value})}
                        className="bg-stone-800 border-stone-700"
                      />
                      {newStaff.type === "hourly" && <span className="text-stone-400">/hr</span>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Pay Frequency</Label>
                    <Select 
                      value={newStaff.pay_frequency} 
                      onValueChange={(v) => setNewStaff({...newStaff, pay_frequency: v})}
                    >
                      <SelectTrigger className="bg-stone-800 border-stone-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-800 border-stone-700">
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddStaff}
              className="bg-purple-500 hover:bg-purple-600"
              disabled={!newStaff.name || !newStaff.email}
            >
              Add Staff Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-400" />
              Process Payout
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Send payment to {selectedStaff?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 text-center">
            <p className="text-stone-400 text-sm mb-2">Amount to pay</p>
            <p className="text-4xl font-bold text-green-400">
              ${selectedStaff?.pending_payout?.toFixed(2) || "0.00"}
            </p>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={processStripePayout}
              className="w-full bg-purple-500 hover:bg-purple-600"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Pay via Stripe Connect
            </Button>
            <Button 
              variant="outline" 
              className="w-full border-stone-600 text-white"
              onClick={() => {
                toast.success("Payment marked as paid (manual)");
                setShowPayoutModal(false);
              }}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Mark as Paid (Cash/Check)
            </Button>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPayoutModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
